// Workspace sidebar — Attio-inspired: dense, hairline borders, workspace switcher up top

const SIDEBAR_AVATAR_HUES = [
  'oklch(0.50 0.14 30)','oklch(0.50 0.13 200)','oklch(0.50 0.13 295)',
  'oklch(0.50 0.13 145)','oklch(0.50 0.14 355)','oklch(0.50 0.13 90)',
];
const userAvatarColor = (key) => {
  let h = 0;
  for (let i = 0; i < (key || '').length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return SIDEBAR_AVATAR_HUES[Math.abs(h) % SIDEBAR_AVATAR_HUES.length];
};

const Sidebar = ({ view, setView, setDealId, onOpenDealsTab, counts, activeUser, activeTeamName, currentRole, canManageBlasts, canViewSignals, canUseDialer, canSee = () => true, onCollapse }) => {
  // The engagement buckets mirror the Engagements page's Mine/Team scope
  // (window.AtlasEngScope, updated live via the atlas-eng-scope event) —
  // showing team-wide 2,227 on hold next to a page saying 645 read as the
  // sidebar being wrong. Each bucket deep-links with the scope it counted.
  const [engScope, setEngScope] = React.useState(() => (window.AtlasEngScope ? window.AtlasEngScope.get(currentRole) : 'team'));
  React.useEffect(() => {
    const onScope = (e) => setEngScope(e.detail === 'team' ? 'team' : 'mine');
    window.addEventListener('atlas-eng-scope', onScope);
    return () => window.removeEventListener('atlas-eng-scope', onScope);
  }, []);
  // Role arrives after mount on a cold boot; re-derive the first-run
  // default then (a stored choice always wins inside get()).
  React.useEffect(() => {
    if (!currentRole || !window.AtlasEngScope || window.AtlasEngScope.hasStored()) return;
    setEngScope(window.AtlasEngScope.get(currentRole));
  }, [currentRole]);
  // `opts.also` — extra view ids that light this row (Team activity owns
  // two views); `opts.trailing` — a node rendered after the badge (the
  // Engagements fold chevron), which stops the click so it doesn't navigate.
  const item = (id, icon, label, badge, opts = {}) => {
    const active = view === id || (opts.also || []).includes(view);
    return (
      <button
        key={id}
        onClick={() => setView(id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '5px 8px',
          borderRadius: 6, fontSize: 12.5, fontWeight: active ? 600 : 500,
          color: active ? 'var(--text)' : 'var(--text-2)',
          background: active ? 'var(--surface-hover)' : 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left',
          transition: 'background .12s, color .12s', height: 28,
        }}
        onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = 'var(--text)'; } }}
        onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
      >
        <span style={{ display: 'inline-flex', color: active ? 'var(--accent)' : 'var(--text-3)' }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        {badge != null && <span style={{ fontSize: 10.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{badge}</span>}
        {opts.trailing}
      </button>
    );
  };

  const sectionLabel = (s) => (
    <div style={{ padding: '12px 10px 4px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s}</div>
  );

  // ---- Nav groups -------------------------------------------------------
  // The flat list grew one row per feature (24 rows + 5 engagement buckets
  // for an admin). Rows are grouped by what they ARE — records you edit,
  // where deals come from, marketing queues, things Atlas computes, admin
  // reports — and each group collapses. A new page must fit one of these
  // groups or become a tab inside an existing page; don't add a sixth.
  //
  // Open/closed is remembered per browser; the first visit uses role-aware
  // defaults; the group that holds the current page always opens so a deep
  // link never lands on a hidden row. ⌘K reaches every page regardless.
  const isAdmin = (currentRole || activeUser?.role || '').toLowerCase() === 'admin';
  const GROUPS_KEY = 'atlas.sidebar.groups';
  const readGroups = () => { try { return JSON.parse(localStorage.getItem(GROUPS_KEY) || '{}') || {}; } catch (_) { return {}; } };
  const [groupPrefs, setGroupPrefs] = React.useState(readGroups);
  const setGroupOpen = (gid, open) => {
    setGroupPrefs((prev) => {
      const next = { ...prev, [gid]: open };
      try { localStorage.setItem(GROUPS_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };
  const groups = [
    { id: 'records',   label: 'Records',   defaultOpen: true,
      ids: ['deals', 'companies', 'people', 'referrals', 'buyers', 'buyer', 'industries', 'industry', 'notes', 'files'] },
    { id: 'sourcing',  label: 'Sourcing',  defaultOpen: true,
      ids: ['prospecting', 'referral-partners', 'gpp', 'events', 'calling'] },
    { id: 'marketing', label: 'Marketing', defaultOpen: !!canManageBlasts,
      ids: ['marketing', 'blast', 'cleanup'] },
    { id: 'insights',  label: 'Insights',  defaultOpen: false,
      ids: ['analytics', 'signals', 'agents'] },
    { id: 'admin',     label: 'Admin',     defaultOpen: false, show: isAdmin,
      ids: ['email-copilot', 'team-pulse', 'daily-reports', 'activity'] },
  ];
  const groupOpen = (g) => (groupPrefs[g.id] != null ? !!groupPrefs[g.id] : g.defaultOpen);
  React.useEffect(() => {
    const g = groups.find((x) => x.ids.includes(view));
    if (g && !groupOpen(g)) setGroupOpen(g.id, true);
  }, [view]);   // eslint-disable-line react-hooks/exhaustive-deps

  const chevron = (open, size = 10) => (
    <Icon.ChevronD size={size} style={{ transition: 'transform .15s', transform: open ? 'none' : 'rotate(-90deg)' }} />
  );
  const groupHeader = (g, open) => (
    <button key={`${g.id}-h`} type="button"
      onClick={() => setGroupOpen(g.id, !open)}
      aria-expanded={open}
      style={{
        display: 'flex', alignItems: 'center', gap: 5, width: '100%', padding: '9px 8px 3px',
        border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', borderRadius: 6,
        fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.06em', textTransform: 'uppercase',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-4)'; }}
    >
      <span style={{ display: 'inline-flex' }}>{chevron(open)}</span>
      <span style={{ flex: 1 }}>{g.label}</span>
    </button>
  );

  // Engagement buckets fold under the Engagements row (they're filters on
  // that one page — the counts belong next to the thing they count).
  const BUCKETS_KEY = 'atlas.sidebar.engBuckets';
  const [bucketsOpen, setBucketsOpen] = React.useState(() => { try { return localStorage.getItem(BUCKETS_KEY) !== '0'; } catch (_) { return true; } });
  const toggleBuckets = (e) => {
    e.stopPropagation();
    setBucketsOpen((o) => { try { localStorage.setItem(BUCKETS_KEY, o ? '0' : '1'); } catch (_) {} return !o; });
  };
  const bucketsChevron = (
    <span role="button" aria-label={bucketsOpen ? 'Hide engagement lists' : 'Show engagement lists'}
      onClick={toggleBuckets}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, marginRight: -4, borderRadius: 4, color: 'var(--text-4)' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}
    >{chevron(bucketsOpen)}</span>
  );
  const engagementBuckets = () => {
    const b = counts?.engagement_breakdown || {};
    const scoped = engScope === 'mine' ? (counts?.engagement_breakdown_mine || {}) : b;
    // Each bucket opens the Engagements tab+scope it counts — landing
    // every click on one default meant "Active (team) 130" opened a
    // list that was neither active nor 130 rows. Two fixed anchors —
    // Mine (always personal) and Active (team) (always team-wide) —
    // while the status buckets follow the page's scope so their
    // numbers match the strip the click lands on.
    const lists = [
      ['Mine',              b.mine,          '#2A6FDB', 'mine',     'mine'],
      ['On hold',           scoped.on_hold,  '#B5571F', 'held',     engScope],
      ['Recently exited',   scoped.exited,   '#A33A3A', 'exited',   engScope],
      ['Active (team)',     b.active,        '#1F8A5B', 'active',   'team'],
      ['Archived',          scoped.archived, '#888',    'archived', engScope],
    ];
    return lists.map(([label, count, c, tabKey, scopeKey]) => (
      <button key={label}
        onClick={() => (onOpenDealsTab ? onOpenDealsTab(tabKey, null, scopeKey) : setView('deals'))}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '4px 8px 4px 22px',
          borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left', height: 26, fontSize: 12, color: 'var(--text-2)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = 'var(--text)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 2, background: c, flexShrink: 0 }} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 10.5, color: 'var(--text-3)' }} className="num">{count != null ? count.toLocaleString() : '—'}</span>
      </button>
    ));
  };

  // Row renderers per view id. Record totals (316k companies…) left the
  // sidebar — every grid's header already says "N of M records"; a count
  // stays only where it is a queue asking for action.
  const rows = {
    'deals':     () => item('deals',     <Icon.Pipeline size={14} />, 'Engagements', null, { trailing: bucketsChevron }),
    'companies': () => item('companies', <Icon.Building size={14} />, 'Companies'),
    'people':    () => item('people',    <Icon.User size={14} />,     'People'),
    'referrals': () => item('referrals', <Icon.Users size={14} />,    'Referrals', counts?.referrals ?? null),
    'buyers':    () => item('buyers',    <Icon.Diamond size={14} />,  'Buyers', null, { also: ['buyer'] }),
    'industries': () => item('industries', <Icon.Layers size={14} />, 'Industries', null, { also: ['industry'] }),
    'notes':     () => item('notes',     <Icon.Doc size={14} />,      'Notes'),
    'files':     () => item('files',     <Icon.External size={14} />, 'Files'),
    'prospecting': () => item('prospecting', <Icon.Sparkle size={14} />, 'Prospecting'),
    'referral-partners': () => item('referral-partners', <Icon.Target size={14} />, 'Referral Partners'),
    'gpp':       () => item('gpp',       <Icon.Users size={14} />,    'GPP Partners'),
    'events':    () => item('events',    <Icon.Pin size={14} />,      'Events'),
    'calling':   () => canUseDialer && item('calling', <Icon.Phone size={14} />, 'Calling'),
    'marketing': () => item('marketing', <Icon.Mail size={14} />,     'Emails'),
    'blast':     () => canManageBlasts && item('blast', <Icon.Mail size={14} />, 'Blast'),
    'cleanup':   () => item('cleanup',   <Icon.Sparkle size={14} />,  'Needs cleaning', counts?.cleanup_total ?? null),
    'analytics': () => item('analytics', <Icon.Grid size={14} />,     'Analytics'),
    'signals':   () => canViewSignals && item('signals', <Icon.Activity size={14} />, 'Signals'),
    'agents':    () => item('agents',    <Icon.Sparkle size={14} />,  'Agents'),
    // Team pulse (weekly) + Daily reports share one page with a Weekly/Daily
    // switch; the row keeps the team-pulse key so both old deep links light it.
    'team-pulse': () => item('team-pulse', <Icon.Users size={14} />, 'Team activity', null, { also: ['daily-reports'] }),
    'daily-reports': () => null,
    'activity':  () => item('activity',  <Icon.Activity size={14} />, 'Data audit'),
    'email-copilot': () => item('email-copilot', <Icon.Bot size={14} />, 'Email Copilot'),
  };

  // Pinned deals — the user's real pins (migration 077), not a heuristic.
  // Empty until the dealmaker pins something from a deal page; the section
  // stays hidden while there's nothing pinned. 'pins-changed' fires from the
  // deal-page toggle so the sidebar reflects a pin/unpin immediately.
  const [pinned, setPinned] = React.useState([]);
  const loadPins = React.useCallback(() => {
    window.AtlasApi.listPinnedDeals().then((res) => {
      const items = Array.isArray(res) ? res : ((res && res.items) || []);
      setPinned(items.map(window.AtlasData.__adaptEngagement));
    }).catch(() => setPinned([]));
  }, []);
  React.useEffect(() => {
    if (!canSee('deals')) return undefined;
    loadPins();
    window.addEventListener('pins-changed', loadPins);
    return () => window.removeEventListener('pins-changed', loadPins);
  }, [loadPins]);

  // Drag-to-reorder pins (migration 104). The row only becomes draggable
  // while the mouse is down on its grip so text-drags on the label don't
  // start a reorder. Optimistic splice, then persist the full id order;
  // a failed PUT reloads server truth.
  const [pinHover, setPinHover] = React.useState(null);
  const [pinArmed, setPinArmed] = React.useState(null);
  const [pinDragging, setPinDragging] = React.useState(null);
  const pinDragIdx = React.useRef(null);
  const dropPin = (toIdx) => {
    const from = pinDragIdx.current;
    setPinDragging(null); setPinArmed(null); pinDragIdx.current = null;
    if (from == null || from === toIdx) return;
    const next = [...pinned];
    const [moved] = next.splice(from, 1);
    next.splice(toIdx, 0, moved);
    setPinned(next);   // optimistic
    window.AtlasApi.reorderPinnedDeals(next.map((d) => d.id))
      .catch((e) => { window.pushToast({ type: 'error', title: 'Reorder failed', body: e?.message || String(e) }); loadPins(); });
  };

  return (
    <aside style={{
      width: 220, flexShrink: 0, borderRight: '1px solid var(--border)',
      background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', height: '100%',
      /* On short viewports the nav + pinned + engagements lists don't fit; without
         this they'd overflow the aside and make the whole document scrollable
         (the "page scrolls down and everything cuts" bug). The middle section
         below scrolls internally instead. */
      overflow: 'hidden',
    }}>
      {/* Workspace switcher */}
      <div style={{ padding: '10px 10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 4 }}>
        <button style={{
          display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, padding: '5px 7px',
          borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer',
        }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, borderRadius: 6,
            background: 'linear-gradient(135deg, oklch(0.55 0.14 255), oklch(0.45 0.18 280))',
            color: '#fff', fontSize: 12.5, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.04em',
          }}>A</span>
          <span style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.2 }}>IndustryPro</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-3)', lineHeight: 1.2, marginTop: 1 }}>Atlas · M&A workspace</div>
          </span>
          <Icon.ChevronD size={12} color="var(--text-3)" />
        </button>
        {onCollapse && (
          <button onClick={onCollapse} title="Hide sidebar" aria-label="Hide sidebar"
            style={{ width: 24, height: 24, borderRadius: 5, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = 'var(--text-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}>
            <Icon.ChevronL size={14} />
          </button>
        )}
      </div>

      {/* Search shortcut */}
      {canSee('search') && <div style={{ padding: '8px 10px 6px' }}>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-cmd'))}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, width: '100%',
            padding: '5px 8px', height: 28, borderRadius: 6, background: 'var(--surface)',
            border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, color: 'var(--text-3)',
            textAlign: 'left', transition: 'border-color .12s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          <Icon.Search size={13} />
          <span style={{ flex: 1 }}>Search…</span>
          <Kbd>⌘K</Kbd>
        </button>
      </div>}

      {/* Scrollable middle — nav + pinned + engagement lists. flex:1/minHeight:0
          so the footer (Settings + user) stays pinned and reachable. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>

      {/* Main nav */}
      <nav style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {canSee('home')     && item('home',      <Icon.Lightning size={14} />, 'Home')}
        {canSee('inbox')    && item('inbox',     <Icon.Check size={14} />,     'Tasks')}
        {canSee('calendar') && item('calendar',  <Icon.Calendar size={14} />,  'Calendar')}
        {/* Integrations lives in Settings → Integrations (same component);
            the old top-level row is gone and #/integrations redirects there. */}
        {groups.filter((g) => g.show !== false && (g.id === 'admin' || g.ids.some((id) => canSee(id)))).map((g) => {
          const open = groupOpen(g);
          return (
            <React.Fragment key={g.id}>
              {groupHeader(g, open)}
              {open && g.ids.filter((id) => g.id === 'admin' || canSee(id) || id === 'buyer').map((id) => (
                <React.Fragment key={id}>
                  {rows[id] ? rows[id]() : null}
                  {id === 'deals' && bucketsOpen && engagementBuckets()}
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </nav>

      {pinned.length > 0 && canSee('deals') && (
        <>
          {sectionLabel('Pinned deals')}
          <div style={{ padding: '0 6px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {pinned.map((d, i) => (
              <button
                key={d.id}
                onClick={() => setDealId(d.id)}
                draggable={pinArmed === d.id}
                onDragStart={(e) => { pinDragIdx.current = i; setPinDragging(d.id); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => { if (pinDragging != null) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); dropPin(i); }}
                onDragEnd={() => { setPinDragging(null); setPinArmed(null); pinDragIdx.current = null; }}
                style={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '4px 8px 4px 18px',
                  borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer',
                  textAlign: 'left', height: 28, color: 'var(--text-2)',
                  opacity: pinDragging === d.id ? 0.4 : 1,
                }}
                onMouseEnter={(e) => { setPinHover(d.id); e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = 'var(--text)'; }}
                onMouseLeave={(e) => { setPinHover((h) => (h === d.id ? null : h)); e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; }}
              >
                {/* Six-dot grip — arms the row's draggable so drags only start here. */}
                <span
                  title="Drag to reorder"
                  onMouseDown={() => setPinArmed(d.id)}
                  onMouseUp={() => setPinArmed(null)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute', left: 3, top: 0, bottom: 0, width: 12,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text-4)', cursor: 'grab',
                    opacity: (pinHover === d.id || pinDragging === d.id) ? 1 : 0,
                    transition: 'opacity .12s',
                  }}
                >
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor" aria-hidden="true">
                    <circle cx="2" cy="2" r="1.1" /><circle cx="6" cy="2" r="1.1" />
                    <circle cx="2" cy="6" r="1.1" /><circle cx="6" cy="6" r="1.1" />
                    <circle cx="2" cy="10" r="1.1" /><circle cx="6" cy="10" r="1.1" />
                  </svg>
                </span>
                <Logo name={d.targetName || d.name} color={window.hashColor(d.targetName || d.name)} size={16} />
                <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      </div>{/* end scrollable middle */}

      <div style={{ marginTop: 'auto', padding: '6px 6px 8px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {canSee('settings') && <button
          onClick={() => setView('settings')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '5px 8px',
            borderRadius: 6, background: view === 'settings' ? 'var(--surface-hover)' : 'transparent',
            border: 'none', cursor: 'pointer', textAlign: 'left', height: 28,
            color: view === 'settings' ? 'var(--text)' : 'var(--text-2)',
            fontSize: 12, fontWeight: view === 'settings' ? 600 : 500,
          }}
          onMouseEnter={(e) => { if (view !== 'settings') { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = 'var(--text)'; } }}
          onMouseLeave={(e) => { if (view !== 'settings') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
        >
          <Icon.Settings size={14} />
          <span style={{ flex: 1 }}>Settings</span>
        </button>}
        {activeUser ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px 2px' }}>
            <Avatar name={activeUser.full_name} color={userAvatarColor(activeUser.email || activeUser.id)} size={24} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeUser.full_name}</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-3)', lineHeight: 1.2, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(activeUser.role || 'member').replace(/_/g, ' ')}
                {activeTeamName ? ` · ${activeTeamName}` : ''}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </aside>
  );
};

window.Sidebar = Sidebar;
